import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { formatMoney } from "@finora/config";
import { api } from "../../lib/api";

export function ProductAttributesSection({ productId }: { productId: string }) {
  const queryClient = useQueryClient();
  const [newGroupName, setNewGroupName] = useState("");

  const { data: groups, isLoading } = useQuery({
    queryKey: ["product-attributes", productId],
    queryFn: () => api.listProductAttributeGroups(productId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["product-attributes", productId] });

  const createGroup = useMutation({
    mutationFn: () => api.createProductAttributeGroup(productId, newGroupName),
    onSuccess: () => {
      invalidate();
      setNewGroupName("");
    },
  });
  const deleteGroup = useMutation({
    mutationFn: (groupId: string) => api.deleteProductAttributeGroup(productId, groupId),
    onSuccess: invalidate,
  });

  if (isLoading) return <p className="text-xs text-slate-400">Učitavanje atributa...</p>;

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <h3 className="text-sm font-semibold text-slate-800">Atributi (npr. Boja, Veličina)</h3>

      {groups?.map((group) => (
        <AttributeGroupCard key={group.id} productId={productId} group={group} onDeleteGroup={() => deleteGroup.mutate(group.id)} />
      ))}

      <div className="flex gap-2">
        <input
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          placeholder="Nova grupa (npr. Boja)"
          className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
        />
        <button
          onClick={() => newGroupName && createGroup.mutate()}
          disabled={!newGroupName || createGroup.isPending}
          className="flex items-center gap-1 rounded-lg border border-border px-3 text-sm hover:bg-app-bg disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Grupa
        </button>
      </div>
    </div>
  );
}

function AttributeGroupCard({
  productId,
  group,
  onDeleteGroup,
}: {
  productId: string;
  group: { id: string; name: string; options: { id: string; name: string; isFree: boolean; priceModifier: string; imageUrl: string | null }[] };
  onDeleteGroup: () => void;
}) {
  const queryClient = useQueryClient();
  const [newOptionName, setNewOptionName] = useState("");
  const [newOptionFree, setNewOptionFree] = useState(true);
  const [newOptionPrice, setNewOptionPrice] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["product-attributes", productId] });

  const createOption = useMutation({
    mutationFn: () =>
      api.createProductAttributeOption(productId, group.id, {
        name: newOptionName,
        isFree: newOptionFree,
        priceModifier: newOptionFree ? undefined : newOptionPrice,
      }),
    onSuccess: () => {
      invalidate();
      setNewOptionName("");
      setNewOptionPrice("");
      setNewOptionFree(true);
    },
  });
  const deleteOption = useMutation({
    mutationFn: (optionId: string) => api.deleteProductAttributeOption(productId, optionId),
    onSuccess: invalidate,
  });
  const updateOptionImage = useMutation({
    mutationFn: (input: { optionId: string; imageUrl: string }) =>
      api.updateProductAttributeOption(productId, input.optionId, { imageUrl: input.imageUrl }),
    onSuccess: invalidate,
  });

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-slate-700">{group.name}</p>
        <button onClick={onDeleteGroup} className="text-slate-400 hover:text-red-600">
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      <div className="space-y-1.5">
        {group.options.map((opt) => (
          <div key={opt.id} className="flex items-center gap-2 rounded-md bg-app-bg px-2 py-1.5 text-sm">
            {opt.imageUrl ? (
              <img src={opt.imageUrl} alt="" className="h-6 w-6 rounded object-cover" />
            ) : (
              <label className="flex h-6 w-6 cursor-pointer items-center justify-center rounded border border-dashed border-border text-[9px] text-slate-400">
                +
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      updateOptionImage.mutate({ optionId: opt.id, imageUrl: String(reader.result) });
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
            )}
            <span className="flex-1">{opt.name}</span>
            <span className="text-xs text-slate-500">
              {opt.isFree ? "besplatno" : `+${formatMoney(opt.priceModifier)}`}
            </span>
            <button onClick={() => deleteOption.mutate(opt.id)} className="text-slate-400 hover:text-red-600">
              <Trash2 className="h-3 w-3" aria-hidden />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          value={newOptionName}
          onChange={(e) => setNewOptionName(e.target.value)}
          placeholder="npr. Crvena"
          className="h-8 flex-1 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
        />
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <input type="checkbox" checked={newOptionFree} onChange={(e) => setNewOptionFree(e.target.checked)} />
          Besplatno
        </label>
        {!newOptionFree && (
          <input
            value={newOptionPrice}
            onChange={(e) => setNewOptionPrice(e.target.value)}
            placeholder="Doplata"
            className="h-8 w-24 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
          />
        )}
        <button
          onClick={() => newOptionName && createOption.mutate()}
          disabled={!newOptionName || createOption.isPending}
          className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-app-bg disabled:opacity-50"
        >
          Dodaj
        </button>
      </div>
    </div>
  );
}
